import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import useFetch from '../../hooks/useFetch';
import { logout } from "../../redux/actions/authActions";

const MySHG = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData, { loading }] = useFetch();

  const isLoggedIn = authState?.isLoggedIn || false;
  const userId = authState.user._id;
  const userName = authState?.user?.name || "User";
  const userThink5WhysCode = authState?.user?.think5whyscode || ""; 
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/shg");
  };

  const [shgList, setShgList] = useState([]);
  const [selectedSHG, setSelectedSHG] = useState(null);
  const [error, setError] = useState("");

  const loadMySHGs = useCallback(() => {
    const config = {
      url: "/shgmaster/myshg",
      method: "post",
      data: { createdBy: userId },
      headers: { Authorization: authState.token }
    };

    fetchData(config)
      .then((res) => {
        if (res.status) {
          setShgList(res.shgList || []);
        } else {
          setShgList([]);
          setError(res.message);
        }
      })
      .catch(() => {
        setError("Failed to load SHG list.");
      });
  }, [userId, authState.token, fetchData]);

  useEffect(() => {
    if (!authState?.isLoggedIn) {
      navigate("/login");
      return;
    }
    loadMySHGs();
  }, [authState.token, loadMySHGs, navigate]);

  const loadDetails = (shgId) => {
    const config = {
      url: "/shgmaster/details",
      method: "post",
      data: { shgId },
      headers: { Authorization: authState.token },
    };

    fetchData(config)
      .then((res) => {
        if (res.status) setSelectedSHG(res.shg);
      })
      .catch(() => setError("Failed to load details"));
  };

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/login"); 
      return;
    }
  }, [isLoggedIn, navigate]);

  return (
    <div className="bg-black min-h-screen text-white">
      <div className="flex flex-col bg-[#1a0b1e] text-pink-100 font-sans">
        <header className="sticky top-0 z-50 bg-[#2a0f2f] border-b border-pink-800 px-4 py-2 flex items-center justify-between shadow-lg">
          <div className="flex flex-col">
            <div className="text-xl lg:text-2xl font-bold text-pink-400">Think5Whys My SHG</div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <div className="text-xs text-left text-pink-300">{userName}</div>
                <button
                  onClick={handleLogout}
                  className="p-2 mt-2 rounded-md bg-rose-600 hover:bg-rose-700 transition"
                  title="Logout"
                >
                  ⏻
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  className="px-3 py-1 rounded-full border border-pink-700 text-sm text-pink-200 hover:bg-pink-900"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate("/signup")}
                  className="px-3 py-1 rounded-full bg-pink-500 text-white text-sm hover:bg-pink-400"
                >
                  Register
                </button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen((s) => !s)}
            className="md:hidden w-6 h-6 flex items-center justify-center text-xl text-pink-300 bg-pink-500"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? "×" : "☰"}
          </button>
        </header>

        {/* Mobile menu content */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#2a0f2f] border-b border-pink-800 px-4 py-2 animate-fadeIn">
            {isLoggedIn ? (
              <div className="flex flex-row items-center justify-center gap-3">
                <span className="text-pink-300 font-medium whitespace-nowrap h-7">
                  Welcome, {userName}
                </span>
                <button
                  onClick={handleLogout}
                  className="rounded-md bg-rose-600 hover:bg-rose-700 w-7 h-7 flex items-center justify-center text-white"
                  title="Logout"
                >
                  ⏻
                </button>
              </div>
            ) : (
              <div className="flex flex-row items-center justify-center gap-6">
                <Link to="/login" className="text-pink-300 hover:text-yellow-300">
                  Login
                </Link>
                <span className="text-pink-500">|</span>
                <Link to="/signup" className="text-pink-300 hover:text-yellow-300">
                  Register
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      <section className="bg-black py-8 px-4">
        <div className="bg-black min-h-screen text-white p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-pink-400">My SHGs</h2>
            <button
              onClick={() => navigate("/shgregister")}
              className="bg-green-600 px-4 py-2 w-20 rounded-lg"
            >
              Register
            </button>
          </div>
          {shgList.length === 0 && (
            <p className="text-yellow-300 text-center mt-10">
              No SHG found. Please register your first SHG.
            </p>
          )}
          <div className="space-y-4 mt-4">
            {shgList.map((shg) => (
              <div
                key={shg._id}
                className="bg-gray-800 p-4 rounded-xl border border-gray-700 cursor-pointer"
                onClick={() => loadDetails(shg._id)}
              >
                <p><span className="text-gray-400">Code:</span> {shg.shgCode}</p>
                <p><span className="text-gray-400">Name:</span> {shg.shgName}</p>
                <p><span className="text-gray-400">Place:</span> {shg.shgAddress}</p>
                <p><span className="text-gray-400">Owner:</span> {authState.user.name}</p>
                <p><span className="text-gray-400">Created:</span> {new Date(shg.dateCreated).toLocaleDateString()}</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/shgmembers?shgId=${encodeURIComponent(shg.shgCode)}&shgName=${encodeURIComponent(shg.shgName)}`
                    );
                  }}
                  className="bg-blue-600 px-3 py-1 rounded-lg mt-2"
                >
                  Show Members
                </button>
              </div>
            ))}
          </div>
          {selectedSHG && (
            <div className="bg-gray-900 p-5 rounded-xl border border-pink-700 mt-6">
              <h3 className="text-lg font-bold text-pink-400 mb-3">SHG Details</h3>

              <div className="space-y-1">
                {Object.entries(selectedSHG).map(([k, v]) => (
                  <p key={k}>
                    <span className="text-gray-400">{k}:</span> {String(v)}
                  </p>
                ))}
              </div>

              <button
                className="bg-red-600 px-4 py-2 rounded-lg mt-4"
                onClick={() => setSelectedSHG(null)}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </section>

      <footer className="bg-[#2a0f2f] border-t border-pink-800 text-pink-300 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs lg:text-sm">
            © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
          </div>
          <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
            <Link to="/about-us" className="text-white hover:text-pink-400">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-pink-400">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-pink-400">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-pink-400">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MySHG;